import type { AdminWebshop } from '@retracedgmbh/modules/companies/contracts';
import type {
  AdminWebshopRow,
  CreateWebshopBody,
  UpdateWebshopBody,
  WebshopConstantsResponse,
  WebshopsListResponse,
} from '@/api/(admin)/webshops/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Admin webshop CRUD. All of it lives on the main gateway under `/api/admin`,
 * so every call takes `API_ROOT_URL`. Leaving `apiFetch`'s default base would
 * resolve to `…/api-service/api/admin/webshops` and 404.
 */

/** The roster. Takes a pre-built query string; paging and filter JSON live in the hook. */
export async function fetchAdminWebshops(
  qs: string,
): Promise<WebshopsListResponse> {
  return apiFetch<WebshopsListResponse>(
    `/api/admin/webshops?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/** Companies that own a webshop, plus every platform in use — both filter option sets. */
export async function fetchWebshopConstants(): Promise<WebshopConstantsResponse> {
  const { data } = await apiFetch<{ data: WebshopConstantsResponse }>(
    '/api/admin/webshops/list-constants',
    {},
    API_ROOT_URL,
  );
  return data;
}

/**
 * Migrated like the writes: path and base both change. Nothing calls this yet —
 * the roster rows carry everything the sheet shows. An unknown id is a 404
 * `webshop_not_found`.
 */
export async function fetchAdminWebshop(id: string): Promise<AdminWebshopRow> {
  const { data } = await apiFetch<{ data: AdminWebshopRow }>(
    `/api/admin/webshops/${id}`,
    {},
    API_ROOT_URL,
  );
  return data;
}

/**
 * Migrated to the modules architecture: both the path and the base change.
 * Leaving the api-service base would resolve to
 * `…/api-service/api/admin/webshops` and 404. A taken shop id is a 409
 * `shop_id_must_be_unique`.
 */
export async function createWebshop(
  body: CreateWebshopBody,
): Promise<AdminWebshop> {
  const { data } = await apiFetch<{ data: AdminWebshop }>(
    '/api/admin/webshops',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    API_ROOT_URL,
  );
  return data;
}

/**
 * The shop id stays the URL segment; renaming it travels as `newId` in the body.
 * A field left out keeps its value, `null` clears it. Unknown id: 404
 * `webshop_not_found`; a `newId` another webshop holds: 409
 * `shop_id_must_be_unique`.
 */
export async function updateWebshop(
  id: string,
  body: UpdateWebshopBody,
): Promise<AdminWebshopRow> {
  const { data } = await apiFetch<{ data: AdminWebshopRow }>(
    `/api/admin/webshops/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
  return data;
}

/**
 * Migrated like `createWebshop`: path and base both change. An unknown id is a
 * 404 `webshop_not_found`; success is an empty 204.
 */
export async function deleteWebshop(id: string): Promise<void> {
  return apiFetch<void>(
    `/api/admin/webshops/${id}`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );
}
